import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Plus } from 'lucide-react';
import { createProjectEntityApi, fetchProjectEntitiesApi, fetchProjectEntityApi } from '../../api/entitiesAdmin.api';
import { deleteProjectFormApi, fetchProjectFormsApi, saveProjectFormWithSchemaApi } from '../../api/formsAdmin.api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Switch } from '../../components/ui/Switch';
import { useI18n } from '../../hooks/useI18n';
import {
  emptySchemaField,
  EntitySchemaEditor,
  fieldsFromEntity,
  serializeSchemaFields
} from './EntitySchemaEditor';
import { FormCard } from './FormCard';
import { FormEditModal } from './FormEditModal';
import { getEntityFields, getFormIdentity } from './requestsUtils';

const mergeFormsWithEntities = (forms, entities) => {
  const byId = new Map();
  const bySlug = new Map();

  (entities || []).forEach((entity) => {
    const identity = getFormIdentity(entity);
    if (identity.id) {
      byId.set(identity.id, entity);
    }
    if (identity.slug) {
      bySlug.set(identity.slug.toLowerCase(), entity);
    }
  });

  return (forms || []).map((form) => {
    const identity = getFormIdentity(form);
    const entity = byId.get(identity.id) || bySlug.get(identity.slug.toLowerCase()) || null;
    const fields = getEntityFields(entity);

    return {
      ...form,
      id: identity.id || form.id,
      slug: identity.slug || form.slug,
      hasSchema: Boolean(entity?.hasSchema ?? entity?.HasSchema) || fields.length > 0,
      fields,
      name: pickEntityName(entity) || identity.slug
    };
  });
};

const pickEntityName = (entity) => entity?.name || entity?.Name || '';

export const FormsList = ({ projectId, forceLoading = false, disableFetch = false }) => {
  const { t } = useI18n();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ displayName: '', authNeed: false, isPrivate: false });
  const [schemaFields, setSchemaFields] = useState([emptySchemaField()]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    displayName: '',
    authNeed: true,
    isPrivate: false
  });
  const [createFields, setCreateFields] = useState([emptySchemaField()]);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [editError, setEditError] = useState('');
  const [createError, setCreateError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadForms = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const items = await fetchProjectFormsApi(projectId);
      let entities = [];
      try {
        entities = await fetchProjectEntitiesApi(projectId);
      } catch {
        entities = [];
      }
      setForms(mergeFormsWithEntities(items, entities));
    } catch (loadError) {
      setForms([]);
      setError(loadError?.message || t('requestsFormsError'));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    if (!projectId || disableFetch) {
      setLoading(Boolean(projectId && disableFetch));
      return;
    }

    loadForms();
  }, [disableFetch, loadForms, projectId]);

  const handleDeleteForm = async () => {
    if (!deleteTarget || deleting) {
      return;
    }

    const formId = getFormIdentity(deleteTarget).id;
    if (!formId) {
      setDeleteError(t('requestsDeleteFormError'));
      return;
    }

    setDeleting(true);
    setDeleteError('');

    try {
      await deleteProjectFormApi({ projectId, formId });
      setDeleteTarget(null);
      setSuccessMessage(t('requestsDeleteFormSuccess'));
      await loadForms();
    } catch (deleteErr) {
      setDeleteError(deleteErr?.message || t('requestsDeleteFormError'));
    } finally {
      setDeleting(false);
    }
  };

  const openEditForm = async (form) => {
    const identity = getFormIdentity(form);
    let nextForm = form;
    let fields = getEntityFields(form);

    if (identity.slug && !fields.length) {
      try {
        const entity = await fetchProjectEntityApi({ projectId, entity: identity.slug });
        fields = getEntityFields(entity);
        nextForm = {
          ...form,
          hasSchema: Boolean(entity?.hasSchema ?? entity?.HasSchema) || fields.length > 0,
          fields
        };
      } catch {
        fields = [];
      }
    }

    setEditTarget(nextForm);
    setEditForm({
      displayName: form.displayName || form.DisplayName || '',
      authNeed: Boolean(form.authNeed ?? form.AuthNeed),
      isPrivate: Boolean(form.isPrivate ?? form.IsPrivate)
    });
    setSchemaFields(fieldsFromEntity(fields));
    setEditError('');
    setSuccessMessage('');
  };

  const openCreateEntity = () => {
    setCreateOpen(true);
    setCreateForm({ name: '', displayName: '', authNeed: true, isPrivate: false });
    setCreateFields([emptySchemaField()]);
    setCreateError('');
    setSuccessMessage('');
  };

  const handleUpdateForm = async (event) => {
    event.preventDefault();

    if (!editTarget || updating) {
      return;
    }

    const identity = getFormIdentity(editTarget);
    if (!identity.id) {
      setEditError(t('requestsUpdateFormError'));
      return;
    }

    setUpdating(true);
    setEditError('');

    try {
      await saveProjectFormWithSchemaApi({
        projectId,
        formId: identity.id,
        slug: identity.slug,
        displayName: editForm.displayName.trim() || editTarget.displayName || editTarget.DisplayName || identity.slug,
        authNeed: editForm.authNeed,
        isPrivate: editForm.isPrivate,
        fields: serializeSchemaFields(schemaFields)
      });
      setEditTarget(null);
      setSuccessMessage(t('requestsUpdateFormSuccess'));
      await loadForms();
    } catch (updateErr) {
      setEditError(updateErr?.message || t('requestsUpdateFormError'));
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateEntity = async (event) => {
    event.preventDefault();

    if (creating) {
      return;
    }

    const name = createForm.name.trim();
    if (!name) {
      setCreateError(t('requestsEntityNameRequired'));
      return;
    }

    setCreating(true);
    setCreateError('');

    try {
      await createProjectEntityApi({
        projectId,
        name,
        displayName: createForm.displayName.trim() || name,
        authNeed: createForm.authNeed,
        isPrivate: createForm.isPrivate,
        fields: serializeSchemaFields(createFields)
      });
      setCreateOpen(false);
      setSuccessMessage(t('requestsCreateEntitySuccess'));
      await loadForms();
    } catch (createErr) {
      setCreateError(createErr?.message || t('requestsCreateEntityError'));
    } finally {
      setCreating(false);
    }
  };

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-950 dark:text-white">{t('databasePanel')}</h1>
        <p className="mt-1 text-sm text-neutral-500">{t('requestsSubtitle')}</p>
      </div>
      <Button type="button" onClick={openCreateEntity}>
        <Plus size={16} />
        {t('requestsCreateEntity')}
      </Button>
    </div>
  );

  if (forceLoading || loading) {
    return (
      <section className="space-y-5">
        {header}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-44 animate-pulse rounded-xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-xl border border-rose-200 bg-rose-50 p-8 dark:border-rose-900/40 dark:bg-rose-900/20">
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 text-center">
          <p className="text-sm text-rose-700 dark:text-rose-200">{error}</p>
          <Button type="button" variant="secondary" onClick={loadForms}>
            {t('requestsRetry')}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="space-y-5">
        {header}
        {successMessage ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
            {successMessage}
          </div>
        ) : null}

        {!forms.length ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-neutral-200 px-6 py-14 text-center dark:border-neutral-800">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
              <ClipboardList size={20} />
            </div>
            <h2 className="mt-4 text-sm font-semibold text-neutral-950 dark:text-white">{t('requestsEmptyTitle')}</h2>
            <p className="mt-2 max-w-md text-sm leading-6 text-neutral-500">{t('requestsEmptyBody')}</p>
            <Button type="button" className="mt-5" onClick={openCreateEntity}>
              <Plus size={16} />
              {t('requestsCreateEntity')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {forms.map((form) => {
              const identity = getFormIdentity(form);
              return (
                <FormCard
                  key={identity.id || identity.slug}
                  form={form}
                  projectId={projectId}
                  onDelete={setDeleteTarget}
                  onEdit={openEditForm}
                />
              );
            })}
          </div>
        )}
      </section>

      <Modal
        open={createOpen}
        onClose={() => {
          if (!creating) {
            setCreateOpen(false);
            setCreateError('');
          }
        }}
        title={t('requestsCreateEntityTitle')}
        closeDisabled={creating}
        panelClassName="flex max-h-[min(90vh,820px)] max-w-2xl flex-col overflow-hidden"
      >
        <form className="flex min-h-0 flex-1 flex-col space-y-3 text-start" onSubmit={handleCreateEntity}>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            <Input
              label={t('requestsEntityName')}
              value={createForm.name}
              disabled={creating}
              dir="ltr"
              className="text-left"
              onChange={(event) => setCreateForm((current) => ({ ...current, name: event.target.value }))}
            />
            <p className="text-xs leading-5 text-neutral-500">{t('requestsEntityNameHint')}</p>
            <Input
              label={t('requestsFormDisplayName')}
              value={createForm.displayName}
              disabled={creating}
              onChange={(event) => setCreateForm((current) => ({ ...current, displayName: event.target.value }))}
            />
            <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-neutral-950 dark:text-white">{t('requestsFormAuthNeed')}</p>
                  <p className="mt-1 text-xs leading-5 text-neutral-500">{t('requestsFormAuthNeedHint')}</p>
                </div>
                <Switch
                  checked={createForm.authNeed}
                  disabled={creating}
                  onChange={(checked) => setCreateForm((current) => ({ ...current, authNeed: checked }))}
                  aria-label={t('requestsFormAuthNeed')}
                />
              </div>
            </div>
            <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-neutral-950 dark:text-white">{t('requestsFormIsPrivate')}</p>
                  <p className="mt-1 text-xs leading-5 text-neutral-500">{t('requestsFormIsPrivateHint')}</p>
                </div>
                <Switch
                  checked={createForm.isPrivate}
                  disabled={creating}
                  onChange={(checked) => setCreateForm((current) => ({ ...current, isPrivate: checked }))}
                  aria-label={t('requestsFormIsPrivate')}
                />
              </div>
            </div>
            <p className="text-sm font-medium text-neutral-950 dark:text-white">{t('requestsEntityFields')}</p>
            <EntitySchemaEditor fields={createFields} disabled={creating} t={t} onChange={setCreateFields} />
            {createError ? <p className="text-sm text-rose-600 dark:text-rose-300">{createError}</p> : null}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)} disabled={creating}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={creating}>
              {creating ? t('requestsCreatingEntity') : t('create')}
            </Button>
          </div>
        </form>
      </Modal>

      <FormEditModal
        open={Boolean(editTarget)}
        onClose={() => {
          setEditTarget(null);
          setEditError('');
        }}
        formState={editForm}
        onFormChange={(patch) => setEditForm((current) => ({ ...current, ...patch }))}
        fields={schemaFields}
        onFieldsChange={setSchemaFields}
        onSubmit={handleUpdateForm}
        saving={updating}
        error={editError}
        t={t}
      />

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) {
            setDeleteTarget(null);
            setDeleteError('');
          }
        }}
        title={t('requestsDeleteFormTitle')}
        closeDisabled={deleting}
        contentClassName="mb-3"
      >
        <div className="space-y-3 text-start">
          <p className="text-sm text-neutral-500">
            {t('requestsDeleteFormConfirm', {
              name: deleteTarget?.displayName || deleteTarget?.DisplayName || getFormIdentity(deleteTarget).slug || ''
            })}
          </p>
          {deleteError ? <p className="text-sm text-rose-600 dark:text-rose-300">{deleteError}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t('cancel')}
            </Button>
            <Button type="button" variant="danger" onClick={handleDeleteForm} disabled={deleting}>
              {deleting ? t('requestsDeletingForm') : t('delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
